import type { ISensemakerOverviewInstrumentRow } from '@brushfire/types'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {
  SENSEMAKER_OVERVIEW_CAMPAIGN_STATUS_BADGE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_INSTRUMENT_STATUS_STYLES,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_BORDER,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_CELL_COLOR,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_HEADER_BG,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_HEADER_COLOR,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_HEADER_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_RADIUS_PX,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_ROW_BORDER,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_ROW_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TYPE_NO_REWARD_STYLES,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TYPE_REWARD_STYLES,
  SENSEMAKER_OVERVIEW_LOI_CARD_BORDER,
  SENSEMAKER_OVERVIEW_LOI_CARD_PADDING_X_PX,
  SENSEMAKER_OVERVIEW_LOI_CARD_RADIUS_PX,
  SENSEMAKER_OVERVIEW_LOI_HEADER_GAP_PX,
  SENSEMAKER_OVERVIEW_LOI_HEADER_PADDING_Y_PX,
  SENSEMAKER_OVERVIEW_LOI_HEADER_TO_STATS_GAP_PX,
  SENSEMAKER_OVERVIEW_LOI_INSTRUMENT_COUNT_BADGE_BG,
  SENSEMAKER_OVERVIEW_LOI_INSTRUMENT_COUNT_BADGE_BORDER,
  SENSEMAKER_OVERVIEW_LOI_INSTRUMENT_COUNT_BADGE_COLOR,
  SENSEMAKER_OVERVIEW_LOI_STAT_LABEL_COLOR,
  SENSEMAKER_OVERVIEW_LOI_STAT_LABEL_SIZE_PX,
  SENSEMAKER_OVERVIEW_LOI_STAT_TILE_BG,
  SENSEMAKER_OVERVIEW_LOI_STAT_TILE_GAP_PX,
  SENSEMAKER_OVERVIEW_LOI_STAT_TILE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_LOI_STAT_TILE_PADDING_PX,
  SENSEMAKER_OVERVIEW_LOI_STAT_TILE_RADIUS_PX,
  SENSEMAKER_OVERVIEW_LOI_STAT_VALUE_COLOR,
  SENSEMAKER_OVERVIEW_LOI_STAT_VALUE_SIZE_PX,
  SENSEMAKER_OVERVIEW_LOI_STATS_TO_TABLE_GAP_PX,
  SENSEMAKER_OVERVIEW_LOI_TITLE_COLOR,
  SENSEMAKER_OVERVIEW_LOI_TITLE_LINE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_LOI_TITLE_SIZE_PX,
} from '../../lib/sensemaker-overview-layout'
import {
  formatOverviewCount,
  formatOverviewDate,
  formatInstrumentStatusLabel,
  formatOverviewUsd,
} from '../../lib/sensemaker-overview-format'
import { TruncatedTooltipText } from '@/shared/components/TruncatedTooltipText'

const INSTRUMENT_TABLE_COLUMNS =
  'minmax(0, 2fr) minmax(0, 0.8fr) minmax(0, 1fr) minmax(0, 0.7fr) minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 1fr)'

const INSTRUMENT_TABLE_COLUMN_GAP_PX = 16

const tableCellSx = {
  minWidth: 0,
  overflow: 'hidden',
} as const

const tableHeaderSx = {
  display: 'grid',
  gridTemplateColumns: INSTRUMENT_TABLE_COLUMNS,
  columnGap: `${INSTRUMENT_TABLE_COLUMN_GAP_PX}px`,
  alignItems: 'center',
  minHeight: `${SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_HEADER_HEIGHT_PX}px`,
  bgcolor: SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_HEADER_BG,
  borderBottom: SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_ROW_BORDER,
  px: '24px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '16px',
  letterSpacing: '0.6px',
  textTransform: 'uppercase' as const,
  color: SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_HEADER_COLOR,
}

function StatusPill({
  bg,
  border,
  color,
  label,
}: Readonly<{ bg: string; border: string; color: string; label: string }>) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: `${SENSEMAKER_OVERVIEW_CAMPAIGN_STATUS_BADGE_HEIGHT_PX}px`,
        px: '11px',
        borderRadius: '999px',
        bgcolor: bg,
        border: `1px solid ${border}`,
        color,
        fontFamily: 'Inter, sans-serif',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '16px',
        width: 'fit-content',
      }}
    >
      {label}
    </Box>
  )
}

interface InstrumentRowProps {
  instrument: ISensemakerOverviewInstrumentRow
}

export function SensemakerOverviewInstrumentRow({ instrument }: Readonly<InstrumentRowProps>) {
  const statusStyle =
    SENSEMAKER_OVERVIEW_INSTRUMENT_STATUS_STYLES[instrument.status] ??
    SENSEMAKER_OVERVIEW_INSTRUMENT_STATUS_STYLES.draft
  const typeStyle = instrument.hasReward
    ? SENSEMAKER_OVERVIEW_INSTRUMENT_TYPE_REWARD_STYLES
    : SENSEMAKER_OVERVIEW_INSTRUMENT_TYPE_NO_REWARD_STYLES

  return (
    <Box
      data-testid={`sensemaker-instrument-row-${instrument.instrumentId}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: INSTRUMENT_TABLE_COLUMNS,
        columnGap: `${INSTRUMENT_TABLE_COLUMN_GAP_PX}px`,
        alignItems: 'center',
        minHeight: `${SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_ROW_HEIGHT_PX}px`,
        px: '24px',
        borderBottom: SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_ROW_BORDER,
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
        lineHeight: '20px',
        color: SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_CELL_COLOR,
        minWidth: 0,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={tableCellSx}>
        <TruncatedTooltipText
          text={instrument.name}
          typographySx={{
            fontFamily: 'inherit',
            fontSize: 'inherit',
            fontWeight: 700,
            color: 'inherit',
          }}
        />
      </Box>
      <Box sx={{ ...tableCellSx, width: 'fit-content', maxWidth: '100%' }}>
        <StatusPill
          bg={statusStyle.bg}
          border={statusStyle.border}
          color={statusStyle.color}
          label={formatInstrumentStatusLabel(instrument.status)}
        />
      </Box>
      <Typography
        sx={{ ...tableCellSx, fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 400 }}
        noWrap
      >
        {formatOverviewDate(instrument.createdAt)}
      </Typography>
      <Typography
        sx={{ ...tableCellSx, fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 400 }}
        noWrap
      >
        {formatOverviewCount(instrument.totalResponses)}
      </Typography>
      <Box sx={tableCellSx}>
        <TruncatedTooltipText
          text={instrument.distributionMethod}
          typographySx={{
            fontFamily: 'inherit',
            fontSize: 'inherit',
            fontWeight: 400,
            color: 'inherit',
          }}
        />
      </Box>
      <Box sx={{ ...tableCellSx, width: 'fit-content', maxWidth: '100%' }}>
        <StatusPill
          bg={typeStyle.bg}
          border={typeStyle.border}
          color={typeStyle.color}
          label={instrument.instrumentTypeTag}
        />
      </Box>
      <Typography
        sx={{ ...tableCellSx, fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 400 }}
        noWrap
      >
        {formatOverviewDate(instrument.completedAt)}
      </Typography>
    </Box>
  )
}

interface LoiProps {
  title: string
  instrumentCount: number
  totalResponses: number
  totalRewardDistributedUsd: number
  instrumentTypePill: string
  instruments: ISensemakerOverviewInstrumentRow[]
}

export function SensemakerOverviewLoiSubCard({
  title,
  instrumentCount,
  totalResponses,
  totalRewardDistributedUsd,
  instrumentTypePill,
  instruments,
}: Readonly<LoiProps>) {
  const statTiles = [
    { label: 'Instrument count', value: String(instrumentCount) },
    { label: 'Total Response Count', value: formatOverviewCount(totalResponses) },
    {
      label: 'Total Reward Distributed',
      value: formatOverviewUsd(totalRewardDistributedUsd, true),
      fullValue: formatOverviewUsd(totalRewardDistributedUsd),
    },
    {
      label: 'Instrument Type',
      value: instrumentTypePill === 'library' ? 'Library' : 'Custom',
      isPill: true,
    },
  ]

  return (
    <Box
      sx={{
        border: SENSEMAKER_OVERVIEW_LOI_CARD_BORDER,
        borderRadius: `${SENSEMAKER_OVERVIEW_LOI_CARD_RADIUS_PX}px`,
        bgcolor: '#fff',
        overflow: 'hidden',
      }}
      data-testid="sensemaker-loi-sub-card"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: `${SENSEMAKER_OVERVIEW_LOI_HEADER_GAP_PX}px`,
          px: `${SENSEMAKER_OVERVIEW_LOI_CARD_PADDING_X_PX}px`,
          py: `${SENSEMAKER_OVERVIEW_LOI_HEADER_PADDING_Y_PX}px`,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: `${SENSEMAKER_OVERVIEW_LOI_TITLE_SIZE_PX}px`,
            fontWeight: 600,
            lineHeight: `${SENSEMAKER_OVERVIEW_LOI_TITLE_LINE_HEIGHT_PX}px`,
            letterSpacing: '-0.4395px',
            color: SENSEMAKER_OVERVIEW_LOI_TITLE_COLOR,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: `${SENSEMAKER_OVERVIEW_CAMPAIGN_STATUS_BADGE_HEIGHT_PX}px`,
            px: '12px',
            borderRadius: '26px',
            bgcolor: SENSEMAKER_OVERVIEW_LOI_INSTRUMENT_COUNT_BADGE_BG,
            border: SENSEMAKER_OVERVIEW_LOI_INSTRUMENT_COUNT_BADGE_BORDER,
            color: SENSEMAKER_OVERVIEW_LOI_INSTRUMENT_COUNT_BADGE_COLOR,
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '16px',
          }}
        >
          {instrumentCount}&nbsp;instruments
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: `${SENSEMAKER_OVERVIEW_LOI_STAT_TILE_GAP_PX}px`,
          px: `${SENSEMAKER_OVERVIEW_LOI_CARD_PADDING_X_PX}px`,
          pb: `${SENSEMAKER_OVERVIEW_LOI_HEADER_TO_STATS_GAP_PX}px`,
        }}
      >
        {statTiles.map((tile) => {
          const tileBody = (
            <Box
              sx={{
                bgcolor: SENSEMAKER_OVERVIEW_LOI_STAT_TILE_BG,
                borderRadius: `${SENSEMAKER_OVERVIEW_LOI_STAT_TILE_RADIUS_PX}px`,
                minHeight: `${SENSEMAKER_OVERVIEW_LOI_STAT_TILE_HEIGHT_PX}px`,
                p: `${SENSEMAKER_OVERVIEW_LOI_STAT_TILE_PADDING_PX}px`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: `${SENSEMAKER_OVERVIEW_LOI_STAT_LABEL_SIZE_PX}px`,
                  fontWeight: 500,
                  lineHeight: '21px',
                  color: SENSEMAKER_OVERVIEW_LOI_STAT_LABEL_COLOR,
                  mb: tile.isPill ? '8px' : 0,
                }}
              >
                {tile.label}
              </Typography>
              {tile.isPill ? (
                <StatusPill
                  bg={SENSEMAKER_OVERVIEW_LOI_INSTRUMENT_COUNT_BADGE_BG}
                  border="#008cd0"
                  color={SENSEMAKER_OVERVIEW_LOI_INSTRUMENT_COUNT_BADGE_COLOR}
                  label={tile.value}
                />
              ) : (
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: `${SENSEMAKER_OVERVIEW_LOI_STAT_VALUE_SIZE_PX}px`,
                    fontWeight: 600,
                    lineHeight: '27px',
                    color: SENSEMAKER_OVERVIEW_LOI_STAT_VALUE_COLOR,
                    mt: 'auto',
                  }}
                >
                  {tile.value}
                </Typography>
              )}
            </Box>
          )

          if ('fullValue' in tile && tile.fullValue) {
            return (
              <Tooltip key={tile.label} title={tile.fullValue} arrow>
                <Box sx={{ display: 'block' }}>{tileBody}</Box>
              </Tooltip>
            )
          }

          return <Box key={tile.label}>{tileBody}</Box>
        })}
      </Box>

      {instruments.length > 0 && (
        <Box
          sx={{
            mx: `${SENSEMAKER_OVERVIEW_LOI_CARD_PADDING_X_PX}px`,
            mb: `${SENSEMAKER_OVERVIEW_LOI_CARD_PADDING_X_PX}px`,
            mt: `${SENSEMAKER_OVERVIEW_LOI_STATS_TO_TABLE_GAP_PX - SENSEMAKER_OVERVIEW_LOI_HEADER_TO_STATS_GAP_PX}px`,
            border: SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_BORDER,
            borderRadius: `${SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_RADIUS_PX}px`,
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          <Box sx={tableHeaderSx}>
            <span>instrument name</span>
            <span>status</span>
            <span>created date</span>
            <span>total responses</span>
            <span>publish method</span>
            <span>instrument type</span>
            <span>completed date</span>
          </Box>
          {instruments.map((inst) => (
            <SensemakerOverviewInstrumentRow key={inst.instrumentId} instrument={inst} />
          ))}
        </Box>
      )}
    </Box>
  )
}
